import {getAreAllNodesClosed, getIsDayAllowed, getIsSelectedDateFuture, useAppSelector} from '@lib/store';
import {useTranslation} from 'react-i18next';

import {Icon} from '@atoms';
import {useModal} from '@hooks';
import {trackEvent} from '@lib/infrastructure';
import {
  LocalDateString,
  LocalDateTimeString,
  Workday,
  WorkspaceReservation,
} from '@lib/store/src/data/timeslots/apiTypes';
import {getQuotaAvailableInWeek} from '@lib/store/src/data/timeslots/selectors';
import {P, Subtitle} from '@quarks';
import {cn} from '@utils';
import {format} from 'date-fns';
import {ComponentProps, ReactNode} from 'react';
import {BookingDetailsCard_timeslots} from 'src/components/organisms/bookWorkspace/BookingDetailsCard/timeslots/BookingDetailsCard_timeslots';
import {useMemoDate} from 'src/components/organisms/calendar/CalendarWeekDay/CalendarWeekDay_timeslots';
import {useEditWorkday} from 'src/components/organisms/calendar/CalendarWeekDay/components/QuickOfficeDayButton_timeslots';
import {TileItem} from '../../TileItem';
import {TileMapPreview_timeslots} from '../../TileMapPreview_timeslots';
import {useReservationDetails} from 'src/components/organisms/bookWorkspace/BookingDetailsCard/timeslots/useReservationDetails';
import {BookWorkspaceCard_timeslots} from 'src/components/organisms/bookWorkspace/BookWorkspaceCard/BookWorkspaceCard_timeslots';

const resolveWorkspaceReservationIcon = (workspaceReservation: WorkspaceReservation | null) => {
  switch (workspaceReservation?.workspace.nodeType) {
    case undefined:
    case 'Desk':
      return 'desk';
    case 'Area':
      return 'area';
    case 'Floor':
      return 'floor';
    default:
      return 'office';
  }
};

// Note: we used to check whether there was still a desk available in
//       any / the current workday's building, but this is not something
//       that makes sense to do at our product's current scale
const useNotAllowedToMakeBookingOnDateReason = (date: LocalDateString): string | null => {
  const {t} = useTranslation();
  const dateAsDate = useMemoDate(date);

  // Check time based validation
  const dateInPast = useAppSelector((state) => {
    const today = state.data.settings.startOfTodayISOString;
    return date < today;
  });

  const dateTooFarInFuture = useAppSelector((state) => getIsSelectedDateFuture(state, dateAsDate));
  const dateNotAllowed = useAppSelector((state) => !getIsDayAllowed(state, dateAsDate));
  const quotaAvailableThisWeek = useAppSelector((state) => getQuotaAvailableInWeek(state, date));
  const areAllBuildingsClosed = useAppSelector((state) => getAreAllNodesClosed(state, dateAsDate));

  if (dateInPast) return t('workplace:NotAllowedToBook');

  if (dateTooFarInFuture) return t('workplace:LocationTooFarAhead');

  if (quotaAvailableThisWeek <= 0) return t('QuotaHasBeenReached');

  if (dateNotAllowed) return t('workplace:NotAllowedToBook');

  if (areAllBuildingsClosed) return t('workplace:AllLocationsClosed');

  return null;
};

export const WorkspaceTile_timeslots = ({
  date,
  workspaceReservation,
  workday,
  ...props
}: ComponentProps<typeof TileItem> & {
  date: string;
  workspaceReservation: WorkspaceReservation | null;
  workday: Workday | null;
}) => {
  const {setModalPages, openModal} = useModal();
  const editWorkday = useEditWorkday().openModal;

  const isGroupBooking = workspaceReservation?.invitationTo !== null || workspaceReservation?.hasInvitations == true;

  const iconName = resolveWorkspaceReservationIcon(workspaceReservation);

  const noBookingAllowedReason = useNotAllowedToMakeBookingOnDateReason(date);

  const details = useReservationDetails(workspaceReservation);

  const handleClick = () => {
    // Go to edit flow
    if (workspaceReservation && workday?.status === 'OfficeDay') {
      setModalPages([
        <BookingDetailsCard_timeslots
          date={date}
          workday={workday}
          workspaceReservation={workspaceReservation}
        />,
      ]);

      openModal();

      trackEvent('CalendarOverview_BookingDetails__Open', {
        date,
        isGroupBooking,
      });

      return;
    }

    // Disabled button
    if (noBookingAllowedReason) return;

    // Redirect to workday booking flow first,
    // Note: Probably only for invite flow (you have a pending reservation without
    //       a workday yet -> we should automate this behavior for the user)
    if (workday?.status !== 'OfficeDay') {
      // Open workday booking flow
      editWorkday(null, date);
      return;
    }

    // Still loading, should be disabled
    // TODO: handle this more gracefully
    if (details.status !== 'fulfilled') return;

    setModalPages([<BookWorkspaceCard_timeslots workday={workday} />]);
    openModal();
  };

  return (
    <>
      {workspaceReservation && <TileMapPreview_timeslots workspaceReservation={workspaceReservation} />}
      <TileItem
        data-testid="workspace-tile"
        onClick={handleClick}
        disabled={details.status !== 'fulfilled' || (!workspaceReservation && noBookingAllowedReason !== null)}
        {...props}>
        <Icon
          className="size-5"
          icon={iconName}
        />
        <div>
          {workspaceReservation ? (
            <ReservationLabel workspaceReservation={workspaceReservation} />
          ) : noBookingAllowedReason ? (
            <TileBody title={noBookingAllowedReason} />
          ) : (
            <NewReservationLabel />
          )}
        </div>

        <span
          className={cn(`size-8 rounded-md flex justify-center items-center`, {
            'bg-connecting-green-500*': workspaceReservation?.status === 'Confirmed',
          })}>
          <Icon
            className={cn(`size-5`, {
              '[&>path]:stroke-2': workspaceReservation?.status === 'Confirmed',
            })}
            color={workspaceReservation?.status === 'Confirmed' ? 'white' : ''}
            icon={workspaceReservation?.status === 'Confirmed' ? 'check' : workspaceReservation ? 'dots' : 'plus'}
          />
        </span>
      </TileItem>
    </>
  );
};

const TileBody = ({
  title,
  timeLabel,
  body,
  subtitle,
}: {
  title: ReactNode;
  body?: ReactNode;
  timeLabel?: ReactNode;
  subtitle?: ReactNode;
}) => {
  return (
    <>
      <div className="flex justify-between">
        <P fontWeight="bold">{title}</P>
        {timeLabel && <P>{timeLabel}</P>}
      </div>
      {body && (
        <P
          fontWeight="regular"
          color="currentColor">
          {body}
        </P>
      )}
      {subtitle && <Subtitle fontWeight="regular">{subtitle}</Subtitle>}
    </>
  );
};

const ReservationLabel = ({workspaceReservation}: {workspaceReservation: WorkspaceReservation}) => {
  const {t} = useTranslation();

  const workspace = workspaceReservation.workspace;

  const anyDeskLabel = t('workplace:AnyAvailableDesk', {count: 1});

  const title =
    workspace.nodeType === 'Desk' || workspace.nodeType === 'Area'
      ? workspace.areaName
      : workspace.nodeType === 'Floor'
      ? workspace.floorName
      : anyDeskLabel;

  const body =
    workspace.nodeType === 'Desk'
      ? `${workspace.floorName}, ${workspace.deskName}`
      : workspace.nodeType === 'Area'
      ? `${workspace.floorName}, ${anyDeskLabel.toLowerCase()}`
      : anyDeskLabel.toLowerCase();

  const subtitle = workspaceReservation.invitationTo
    ? t('screen:WorkspaceInvitationDetails')
    : workspaceReservation.hasInvitations
    ? t('workplace:GroupBooking')
    : null;

  return (
    <TileBody
      title={title}
      timeLabel={
        <ReservationTimeLabel
          start={workspaceReservation.localStart}
          end={workspaceReservation.localEnd}
        />
      }
      body={body}
      subtitle={subtitle}
    />
  );
};

const NewReservationLabel = ({}) => {
  const {t} = useTranslation();
  const title = t('workplace:BookAWorkspace');
  return <TileBody title={title} />;
};

export const ReservationTimeLabel = ({start, end}: {start: LocalDateTimeString; end: LocalDateTimeString}) => {
  const {t} = useTranslation();

  if (/T00:00(:00)?$/.test(start) && /T23:59(:00)?$/.test(end)) {
    return null;
    // return t('meeting:AllDay');
  }

  const startHHmm = format(new Date(start), 'HH:mm');
  const endHHmm = format(new Date(end), 'HH:mm');

  return `${startHHmm} - ${endHHmm}`;
};
